import type { GetIssueTemplateResponseDto } from '@issuemanager/api-client';
import {
  StyledDataGrid,
  useDataGridContext,
} from '@issuemanager/apps/client/components';
import {
  useAllQueries,
  useMonitorIssueTemplatesListLoad,
} from '@issuemanager/apps/client/hooks';
import type { GridRowParams } from '@mui/x-data-grid-pro';
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIssueTemplates } from '../../features/issue-templates/hooks/use-issue-templates';
import { paths } from '../routes/paths';
import { IssueTemplatesEmptyState } from './components/empty-state';
import { IssueTemplatesGridToolbar } from './components/issue-templates-grid-toolbar';
import { useIssueTemplateGridColumns } from './use-issue-template-grid-columns';
import { useIssueTemplateGridQueryParams } from './use-issue-template-grid-query-params';

export function IssueTemplateGrid() {
  const navigate = useNavigate();
  const dataGridContext = useDataGridContext();
  const queryParams = useIssueTemplateGridQueryParams(
    dataGridContext.filterModel,
    dataGridContext.sortModel,
  );
  const monitorIssueTemplatesListLoad = useMonitorIssueTemplatesListLoad();

  const issueTemplates = useIssueTemplates({
    page: dataGridContext.paginationModel.currentPage,
    limit: dataGridContext.paginationModel.pageSize,
    filter: { ...queryParams.filter, searchTerm: dataGridContext.searchTerm },
    sort: queryParams.sort,
  });
  useAllQueries({
    queries: [issueTemplates],
    onSuccess: () => {
      monitorIssueTemplatesListLoad.stop();
    },
    onError: () => {
      monitorIssueTemplatesListLoad.error();
    },
  });
  useEffect(() => {
    if (!issueTemplates.isSuccess) return;
    dataGridContext.setPaginationModel((prevState) => ({
      ...prevState,
      total: issueTemplates.data.meta.itemCount,
    }));
  }, [issueTemplates.isSuccess]);

  const handleRowClick = useCallback(
    (issueTemplate: GetIssueTemplateResponseDto) => {
      const searchParams = new URLSearchParams();
      searchParams.append('issueTypeId', issueTemplate.issueType.id);
      if (issueTemplate.issueSubtype?.id) {
        searchParams.append('issueSubtypeId', issueTemplate.issueSubtype.id);
      }
      navigate(
        `/${paths.settings.index}/${paths.issueTemplates.index}/${
          issueTemplate.slug
        }?${searchParams.toString()}`,
      );
    },
    [],
  );

  const columns = useIssueTemplateGridColumns();

  return (
    <div data-testid='issueTemplate-grid'>
      <StyledDataGrid
        error={issueTemplates.isError}
        loading={issueTemplates.isFetching}
        onRowClick={(params: GridRowParams) => {
          handleRowClick?.(params.row);
        }}
        columns={columns}
        rows={issueTemplates.data?.data ?? []}
        components={{
          NoRowsOverlay: IssueTemplatesEmptyState,
          Toolbar: IssueTemplatesGridToolbar,
        }}
      />
    </div>
  );
}
